import { useEffect, useRef, useState } from 'react';
import { authHeaders, forceRelogin } from '../api';
import { useToast } from './shared';

const cache = new Map(); // url -> objectURL (photos need the auth header, so <img src> can't load them directly)

// Shrink big camera photos before upload (max side 1600px, JPEG)
async function shrink(file) {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type) || file.size < 400 * 1024) return file;
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
    const c = document.createElement('canvas');
    c.width = Math.round(bmp.width * scale); c.height = Math.round(bmp.height * scale);
    c.getContext('2d').drawImage(bmp, 0, 0, c.width, c.height);
    const blob = await new Promise(r => c.toBlob(r, 'image/jpeg', 0.85));
    return blob && blob.size < file.size ? blob : file;
  } catch (e) { return file; }
}

function Lightbox({ src, onClose }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, cursor: 'zoom-out' }}>
      <img src={src} alt="" style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8 }} />
    </div>
  );
}

/**
 * kind: 'device' | 'client'; id; version: photo_at (null = no photo)
 * canEdit: show upload/change; size: px of the square thumbnail
 */
export default function Photo({ kind, id, version, canEdit, canDelete, size = 64, icon = '📷', onChange, style }) {
  const [src, setSrc] = useState(null);
  const [big, setBig] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const toast = useToast();
  const url = `/api/photos/${kind}/${id}?v=${encodeURIComponent(version || '')}`;

  useEffect(() => {
    let alive = true;
    if (!version) { setSrc(null); return; }
    if (cache.has(url)) { setSrc(cache.get(url)); return; }
    fetch(url, { headers: authHeaders() }).then(async r => {
      if (r.status === 401) return forceRelogin();
      if (!r.ok) return;
      const o = URL.createObjectURL(await r.blob());
      cache.set(url, o);
      if (alive) setSrc(o);
    }).catch(() => {});
    return () => { alive = false; };
  }, [url, version]);

  async function upload(e) {
    const f = e.target.files[0]; e.target.value = '';
    if (!f) return;
    if (!f.type.startsWith('image/')) return toast('اختار صورة', 'error');
    setBusy(true);
    try {
      const body = await shrink(f);
      const r = await fetch(`/api/photos/${kind}/${id}`, { method: 'POST', headers: { 'Content-Type': body.type || f.type, ...authHeaders() }, body });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'Upload failed');
      toast('اتحفظت الصورة'); onChange && onChange(j.photo_at);
    } catch (err) { toast(err.message, 'error'); }
    finally { setBusy(false); }
  }
  async function remove(e) {
    e.stopPropagation();
    if (!window.confirm('مسح الصورة؟')) return;
    await fetch(`/api/photos/${kind}/${id}`, { method: 'DELETE', headers: authHeaders() });
    onChange && onChange(null);
  }

  const box = {
    width: size, height: size, borderRadius: 10, flexShrink: 0, overflow: 'hidden', position: 'relative',
    background: 'var(--bg2)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: Math.max(16, size / 3), color: 'var(--text3)', ...style,
  };
  return (
    <>
      <div style={box} onClick={e => { e.stopPropagation(); if (src) setBig(true); else if (canEdit) inputRef.current.click(); }}
        title={src ? 'كبّر الصورة' : canEdit ? 'أضف صورة' : ''}>
        {src ? <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'zoom-in' }} />
          : <span style={{ cursor: canEdit ? 'pointer' : 'default' }}>{busy ? '…' : icon}</span>}
        {canEdit && (
          <button type="button" onClick={e => { e.stopPropagation(); inputRef.current.click(); }} title={src ? 'غيّر الصورة' : 'أضف صورة'}
            style={{ position: 'absolute', bottom: 2, insetInlineEnd: 2, width: 24, height: 24, borderRadius: 12, border: 'none',
              background: 'rgba(0,0,0,.6)', color: '#fff', fontSize: 12, cursor: 'pointer', lineHeight: '24px', padding: 0 }}>
            {busy ? '…' : '📷'}</button>
        )}
        {canDelete && src && (
          <button type="button" onClick={remove} title="مسح"
            style={{ position: 'absolute', top: 2, insetInlineEnd: 2, width: 20, height: 20, borderRadius: 10, border: 'none',
              background: 'rgba(0,0,0,.6)', color: '#fff', fontSize: 11, cursor: 'pointer', lineHeight: '20px', padding: 0 }}>✕</button>
        )}
        <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={upload} />
      </div>
      {big && src && <Lightbox src={src} onClose={() => setBig(false)} />}
    </>
  );
}
